import { useEffect, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Button, Popover, Spinner, Switch } from '@arkestro/arkestro-design-system';

import { ID } from 'types';
import { persistNsbConfig } from '@api/NsbConfigApi';
import { NsbFlavorsResponse } from '@api/SmartBaselineFlavorsApi';
import { appliedSmartBaselineKeys } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/appliedSmartBaselineStore';
import { triggerBenchmarkRefresh } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/benchmarkRefreshStore';
import { isFullFlavors } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/nsbFlavorsCache';
import { ensureFullFlavors } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/nsbFullFlavorsLoader';
// eslint-disable-next-line import/no-cycle -- runtime-only (handler) usage; QuickPicker <-> BenchmarkRenderer cycle is intentional (co-located, never crosses a PR boundary)
import {
  FLAVOR_DEFS,
  FlavorDef,
  getEffectivePriceForFlavor,
  isOverrideAllowed,
  getNegotiationColIdx,
  getPriceColIdx,
  setBaselineUserFlavor,
  setEventOverrideAllowed,
  showSmartBaselineIntelModal,
  subscribeLotOverride,
  triggerBaselineRefresh,
  userSelectedFlavorKeys,
} from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer';
import {
  isSmartBaselineApplyAllowed,
  subscribeNsbSelection,
} from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/nsbSelectionStore';
import { setNsbRowConfigColumn } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer/nsbRowConfigStore';
import { displayCurrencyWithPrecision } from '@utils/currency';
import styles from './SmartBaselineQuickPicker.module.scss';
import type { IntelModalState } from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer';

// Step 1 of the flow: a lightweight popover anchored to the clicked price link. Shows Mirror
// (baseline column only) + the top recommended flavors with confidence/price, and a
// "See Analysis and Other Options" link that opens the full modal (step 2). Anchored via a
// controlled ADS Popover (see SmartBaselineQuickPickerContainer) so it never wraps a HOT cell
// child (avoids the antd-in-HOT rendering issues).

export interface QuickPickerState extends IntelModalState {
  anchorRect: { bottom: number; left: number; right: number; top: number };
  columnLabel: string;
  eventId?: ID;
  isBaseline: boolean;
}

let setter: ((state: QuickPickerState | null) => void) | null = null;

export const registerQuickPickerSetter = (fn: ((state: QuickPickerState | null) => void) | null): void => {
  setter = fn;
};

export const showSmartBaselineQuickPicker = (state: QuickPickerState): void => setter?.(state);
export const hideSmartBaselineQuickPicker = (): void => setter?.(null);

// Confidence color coding (refined in 4c): high = green, mid = orange, low = red.
const confidenceClassName = (conf: number): string => {
  if (conf >= 80) return styles.confHigh;
  if (conf >= 50) return styles.confMid;
  return styles.confLow;
};

const TOP_N = 3;
// Two decimals until the client-precision follow-up noted on the option rows below lands.
const QUICK_PICKER_PRICE_PRECISION = 2;

const QuickPicker = ({ state, onClose }: { onClose: () => void; state: QuickPickerState }) => {
  const { column, columnLabel, columnSettings, currency, eventId, flavors, hotRef, isBaseline, lotId, nsbItem, row, sbKey } = state;
  const { t } = useTranslation('common');
  const [, forceRender] = useState(0);
  useEffect(() => subscribeLotOverride(() => forceRender(n => n + 1)), []);
  useEffect(() => subscribeNsbSelection(() => forceRender(n => n + 1)), []);
  // Apply is allowed only when this column's event method is Smart Baseline AND the lot permits overrides.
  const allowed = isSmartBaselineApplyAllowed(column) && isOverrideAllowed(lotId, column);

  // Ranking options needs confidence scores, which the grid's slim fetch omits. Fetch the full
  // payload for this one item now the popup is open; prefer it over the slim snapshot once it lands.
  const [fullFlavors, setFullFlavors] = useState<NsbFlavorsResponse | null>(
    isFullFlavors(flavors) ? flavors : null,
  );
  const [isLoadingFull, setIsLoadingFull] = useState(false);
  // nsbItem carries the resolved item_name_to_use on `item_name`, which is also the cache key.
  const resolvedItemName = nsbItem?.item_name ?? null;
  const cacheKey = resolvedItemName ? `byname__${resolvedItemName}` : null;

  useEffect(() => {
    if (isFullFlavors(flavors)) { setFullFlavors(flavors); return; }
    if (!cacheKey || !resolvedItemName) return;
    setIsLoadingFull(true);
    ensureFullFlavors(cacheKey, resolvedItemName)
      .then(result => { if (result) setFullFlavors(result); })
      .finally(() => setIsLoadingFull(false));
  }, [cacheKey, resolvedItemName]);

  // FULL payload only — a slim snapshot would rank every option at 0% and save confScore: 0.
  const precomputes = fullFlavors?.precomputes;

  // Available flavors with a usable price, ranked by net confidence (desc).
  const ranked = FLAVOR_DEFS
    .map(def => {
      if (!precomputes) return null;
      const { price } = getEffectivePriceForFlavor(precomputes, def.id);
      if (price == null || price <= 0) return null;
      // `Math.round(undefined * weight)` is NaN, which would sort randomly and paint a NaN badge.
      const netConf = precomputes[def.netConfKey] as number | null | undefined;
      const rawConf = precomputes[def.confKey] as number | null | undefined;
      const derived = typeof rawConf === 'number' ? Math.round(rawConf * def.weight) : null;
      const conf = netConf ?? derived ?? 0;
      return { conf, def, price };
    })
    .filter((x): x is { conf: number; def: FlavorDef; price: number } => x !== null)
    .sort((a, b) => b.conf - a.conf);

  // The per-item "Recommended" pick — resolves to THIS row's own recommended flavor. Applying it stores
  // the RESOLVED flavor (id + label), so the row-level type shows that method, never the word "Recommended".
  const recPrice = precomputes ? getEffectivePriceForFlavor(precomputes, 'recommended').price : null;
  const recFlavorId = precomputes?.recommended_flavor === 'F3-5' ? 'F3' : precomputes?.recommended_flavor;
  const recDef = recFlavorId ? FLAVOR_DEFS.find(f => f.id === recFlavorId) : undefined;
  const recConf = precomputes?.recommended_conf_score ?? 0;

  const applyFlavor = (def: FlavorDef, price: number, conf: number): void => {
    if (!allowed) return;
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    // Record the applied flavor BEFORE writing the cell. setDataAtCell can fire the change +
    // re-render synchronously, which runs the renderer's manual-entry detector; if that ran while the
    // applied meta still held the OLD flavor, it would see "new value ≠ old applied price" and wrongly
    // flip the row to Manual Entry. Setting the meta first keeps value and applied in sync.
    if (column === 'baseline') {
      // Baseline column owns a SEPARATE per-row flavor store (the negotiation column's
      // appliedSmartBaselineKeys shares the same sbKey, so they must not be conflated).
      const priceColIdx = getPriceColIdx(columnSettings);
      if (priceColIdx >= 0) {
        setBaselineUserFlavor(sbKey, def.label, price);
        setNsbRowConfigColumn(sbKey, 'baseline', def.id, lotId);
        hot.setDataAtCell(row, priceColIdx, price);
        triggerBaselineRefresh(row);
      }
    } else {
      const negColIdx = getNegotiationColIdx(columnSettings);
      if (negColIdx >= 0) {
        userSelectedFlavorKeys.add(sbKey);
        appliedSmartBaselineKeys.set(sbKey, { confScore: conf, flavorLabel: def.label, price });
        setNsbRowConfigColumn(sbKey, 'negotiation', def.id, lotId);
        hot.setDataAtCell(row, negColIdx, price);
        // The non-split `nsb_benchmark` column is read-only, so setDataAtCell is a no-op there and won't
        // re-render the cell from its (unchanged) value — the cell reads its price/label from
        // appliedSmartBaselineKeys instead. Kick this row's renderer so it reflects the new pick.
        triggerBenchmarkRefresh(row);
      }
    }
    onClose();
  };

  const applyRecommended = (): void => {
    if (!allowed || recPrice == null || !recDef) return;
    // Apply the RESOLVED flavor so the row shows the actual method for this item, not "Recommended".
    applyFlavor(recDef, recPrice, recConf);
  };

  // FULL payload only, same reason the options list needs it: the modal ranks and PERSISTS a confidence
  // score, and a slim snapshot has none — it would draw blank charts and save confScore: NaN.
  const openAnalysis = (): void => {
    if (!fullFlavors) return;
    showSmartBaselineIntelModal({
      column, columnLabel, columnSettings, currency, flavors: fullFlavors, hotRef, lotId, nsbItem, row, sbKey,
    });
    onClose();
  };

  // Turn this column's event-level override ON (applies to all rows in all lots) and persist it so it
  // survives reload. The store notify() re-renders this picker, `allowed` flips true, and the banner +
  // this toggle disappear — there is intentionally no row-level OFF (that's done in the method modal).
  const enableOverrides = (): void => {
    setEventOverrideAllowed(column, true);
    if (eventId != null) {
      const config = column === 'baseline' ? { allow_baseline_override: true } : { allow_negotiation_override: true };
      persistNsbConfig(eventId, config).catch(() => undefined);
    }
  };

  return (
    <div className={styles.popover} role='dialog'>
      <div className={styles.header}>
        {columnLabel} &gt; {nsbItem.label || nsbItem.item_name}
      </div>

      {/* When applying is blocked, say WHY instead of silently greying the options. */}
      {!allowed && (
        <div className={styles.banner}>
          {isSmartBaselineApplyAllowed(column) ? (
            <div className={styles.bannerRow}>
              <span>{t('tlc_smart_baseline.quick_picker.overrides_off')}</span>
              <Switch checked={false} onChange={(checked: boolean) => { if (checked) enableOverrides(); }} />
            </div>
          ) : (
            t('tlc_smart_baseline.quick_picker.not_smart_baseline')
          )}
        </div>
      )}

      {isBaseline && (
        <Button className={styles.mirrorRow} onClick={onClose} type='text'>
          <span className={styles.rowContent}>
            <span>{t('tlc_smart_baseline.quick_picker.mirror_starting_price')}</span>
            <span>✓</span>
          </span>
        </Button>
      )}

      <div className={styles.sectionLabel}>{t('tlc_smart_baseline.quick_picker.recommended_options')}</div>
      {/* Options and scores are fetched on open, not at page load — see nsbFullFlavorsLoader. */}
      {isLoadingFull && <Spinner />}
      {/* The fetch resolves empty rather than throwing, so without this the buyer gets a label and
          nothing under it on a row whose price the grid is already painting. */}
      {!isLoadingFull && !precomputes && (
        <div className={styles.banner}>{t('tlc_smart_baseline.quick_picker.details_unavailable')}</div>
      )}
      {recDef && recPrice != null && recPrice > 0 && (
        <Button
          className={allowed ? styles.optionRow : styles.optionRowDisabled}
          disabled={!allowed}
          onClick={applyRecommended}
          type='text'
        >
          <span className={styles.rowContent}>
            <span>{t('tlc_smart_baseline.flavors.recommended.full_label')}</span>
            <span className={styles.optionMeta}>
              <span className={confidenceClassName(recConf)}>{recConf}%</span>
              {/* FOLLOW-UP (separate PR): the price is formatted with toFixed(2), but it should honor
                  the client company's configured price precision. This applies across ALL smart
                  baseline flavors — the ranked rows below and the other baseline surfaces on the
                  event — not just Recommended. Deferred to a dedicated precision PR so the whole
                  event flow follows one convention (raised in review by @brentrowswell). */}
              <span>{displayCurrencyWithPrecision(recPrice, currency, QUICK_PICKER_PRICE_PRECISION)}</span>
            </span>
          </span>
        </Button>
      )}
      {ranked.slice(0, TOP_N).map(({ conf, def, price }) => (
        <Button
          key={def.id}
          className={allowed ? styles.optionRow : styles.optionRowDisabled}
          disabled={!allowed}
          onClick={() => applyFlavor(def, price, conf)}
          type='text'
        >
          <span className={styles.rowContent}>
            <span>{t(def.fullLabel)}</span>
            <span className={styles.optionMeta}>
              <span className={confidenceClassName(conf)}>{conf}%</span>
              {/* toFixed(2): same client-precision follow-up as the Recommended row above — a separate
                  PR will move all these to the company's configured precision. */}
              <span>{displayCurrencyWithPrecision(price, currency, QUICK_PICKER_PRICE_PRECISION)}</span>
            </span>
          </span>
        </Button>
      ))}

      <div className={styles.footer}>
        <Button
          className={styles.footerLink}
          disabled={!fullFlavors}
          onClick={openAnalysis}
          type='link'
        >
          {t('tlc_smart_baseline.quick_picker.see_analysis')}
        </Button>
      </div>
    </div>
  );
};

export const SmartBaselineQuickPickerContainer = () => {
  const [state, setState] = useState<QuickPickerState | null>(null);
  // Survives `state` going null so the closing overlay keeps a position to animate out from. Written in
  // an effect rather than during render: on the render where `state` becomes null the ref still holds
  // the value stored by the previous render's effect, which is exactly the rect to animate out from.
  const lastAnchorRef = useRef<QuickPickerState['anchorRect'] | null>(null);
  useEffect(() => { if (state) lastAnchorRef.current = state.anchorRect; }, [state]);

  useEffect(() => {
    registerQuickPickerSetter(setState);
    return () => registerQuickPickerSetter(null);
  }, []);

  // Popover's own trigger='click' handles outside-click dismissal; Escape isn't built in, so handle it here.
  useEffect(() => {
    if (!state) return undefined;
    const close = (e: KeyboardEvent): void => { if (e.key === 'Escape') setState(null); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [state]);

  // The Popover stays MOUNTED and is driven by `open` — it must not be torn out with an early
  // `return null` on close. Unmounting it mid-leave-motion left its portaled overlay orphaned in
  // document.body: a grey panel still painted on screen, non-interactive, over a live page.
  // Keeping it mounted lets the close motion finish and antd clean its own portal up.
  //
  // `lastAnchor` holds the most recent rect so the overlay still has a sane position to animate
  // out from after `state` has already gone null.
  const anchorRect = state?.anchorRect ?? lastAnchorRef.current;
  const anchor = anchorRect
    ? { height: anchorRect.bottom - anchorRect.top, left: anchorRect.left, top: anchorRect.top }
    : { height: 0, left: 0, top: 0 };

  return (
    <Popover
      content={state ? <QuickPicker onClose={() => setState(null)} state={state} /> : null}
      destroyTooltipOnHide
      onOpenChange={open => { if (!open) setState(null); }}
      open={!!state}
      placement='bottom'
      trigger='click'
      zIndex={1100}
    >
      {/* Invisible trigger pinned to the clicked price link's rect — Popover measures it to position/flip itself. */}
      <span data-testid='quick-picker-anchor' style={{ ...anchor, position: 'fixed', width: 0 }} />
    </Popover>
  );
};
